import React from 'react'
import { AsyncContentProgress, Button, Card, CardContent, Modal } from '@/shared/ui'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { MoreHorizontal } from 'lucide-react'
import { useUserDateFormatter } from '@/modules/auth/hooks/useUserTimezone'
import { normalizeAuthorName } from '@/shared/utils/authorName'
import { useToast } from '@/shared/ui/Toast'
import type { AnalysisItem } from '../services/analysisService'
import { deleteAnalysis, updateAnalysis } from '../services/analysisService'
import type { AnalysisCommunitySummary } from '../services/analysisCommunityService'
import { AnalysisCommunityEntry } from './AnalysisCommunityEntry'
import { NewAnalysisModal, type NewAnalysisModalSubmitParams } from './NewAnalysisModal'
import { deleteAnalysisPostAttachmentsByIds } from '../services/analysisPostAttachmentsService'
import { uploadAndAddAnalysisPostAttachments } from '../services/analysisPostAttachmentsUploadService'
import { AnalysisPostContent } from './AnalysisPostContent'
import { AnalysisPostAttachments } from './AnalysisPostAttachments'
import { buildLinkPreview, extractFirstUrlFromHtml } from '@/modules/comunidade/services/linkPreviewService'
import { CommunityLinkPreviewCard } from '@/modules/comunidade/components/CommunityLinkPreviewCard'
import { AnalysisCardAuthorMeta } from './AnalysisCardAuthorMeta'
import type { CommunitySummary } from '@/modules/comunidade/services/communityService'
import { OpportunityCommunityEntry } from '@/modules/comunidade/components/OpportunityCommunityEntry'
import type { OpportunityItem } from '@/modules/oportunidades/types'
import { OpportunityCompactCard } from '@/modules/oportunidades/components/OpportunityCompactCard'
import { AnalysisLinkedOpportunityHistory } from './AnalysisLinkedOpportunityHistory'
import { AnalysisTimelineHistory } from './AnalysisTimelineHistory'

type AnalysisCardProps = {
  analysis: AnalysisItem
  communityEnabled: boolean
  canManagePosts: boolean
  thermoAllowed: boolean
  noteForMaster?: string
  linkedOpportunity?: OpportunityItem | null
  analysisCommunitySummary?: AnalysisCommunitySummary | null
  opportunityCommunitySummary?: CommunitySummary | null
  focusMessageId?: number | null
  timelineRootId?: number | null
  showTimelineOverlay?: boolean
  onChanged?: () => void
}

export function AnalysisCard({
  analysis,
  communityEnabled,
  canManagePosts,
  thermoAllowed,
  noteForMaster,
  linkedOpportunity,
  analysisCommunitySummary,
  opportunityCommunitySummary,
  focusMessageId,
  timelineRootId,
  showTimelineOverlay,
  onChanged,
}: AnalysisCardProps) {
  const { formatUserDateTime } = useUserDateFormatter()
  const { add: toast } = useToast()
  const displayAuthorName = normalizeAuthorName(analysis.authorName)

  const [editOpen, setEditOpen] = React.useState(false)
  const [deleteOpen, setDeleteOpen] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const linkPreview = React.useMemo(() => {
    const url = extractFirstUrlFromHtml(String(analysis.contentHtml || ''))
    if (!url) return null
    return buildLinkPreview(url)
  }, [analysis.contentHtml])

  async function handleEdit(params: NewAnalysisModalSubmitParams) {
    await updateAnalysis(analysis.id, {
      title: params.title,
      html: params.html,
      linkedOpportunityId: params.linkedOpportunityId,
      linkedTimelineRootId: params.linkedTimelineRootId,
      courtesyEnabled: params.courtesyEnabled,
    })

    if (params.removeAttachmentIds.length > 0) {
      await deleteAnalysisPostAttachmentsByIds(params.removeAttachmentIds)
    }

    if (params.newAttachments.length > 0) {
      const res = await uploadAndAddAnalysisPostAttachments(analysis.id, params.newAttachments)
      if (res.failedCount > 0) {
        toast({
          title: 'Alguns anexos falharam',
          description: `Anexos enviados: ${res.uploadedCount}. Falhas: ${res.failedCount}.`,
          variant: 'warning',
          soft: true,
        })
      }
    }

    if (onChanged) onChanged()
  }

  async function handleConfirmDelete() {
    if (deleting) return
    setDeleting(true)
    try {
      await deleteAnalysis(analysis.id)
      if (onChanged) onChanged()
      setDeleteOpen(false)
    } catch (e: any) {
      toast({
        title: 'Falha ao excluir',
        description: e?.message || 'Tente novamente em instantes.',
        variant: 'error',
      })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-base font-semibold leading-tight text-foreground">
              {analysis.title}
            </div>
          </div>
          {canManagePosts && (
            <div className="ml-2 flex items-start">
              <DropdownMenu
                align="end"
                trigger={(
                  <button
                    type="button"
                    className="inline-flex items-center rounded p-1 hover:bg-muted/30"
                  >
                    <MoreHorizontal className="h-5 w-5 text-[#434343]" />
                  </button>
                )}
                items={[
                  {
                    label: 'Editar',
                    onSelect: () => setEditOpen(true),
                  },
                  {
                    label: 'Excluir',
                    onSelect: () => setDeleteOpen(true),
                  },
                ]}
              />
            </div>
          )}
        </div>

        <div className="mt-3 min-w-0 space-y-3">
          <AnalysisPostContent html={analysis.contentHtml} />

          <AnalysisPostAttachments attachments={analysis.attachments} />

          {linkPreview && (
            <div>
              <CommunityLinkPreviewCard preview={linkPreview} />
            </div>
          )}

          {analysis.linkedOpportunityId !== null ? (
            linkedOpportunity ? (
              <OpportunityCompactCard
                item={linkedOpportunity}
                thermoAllowed={thermoAllowed}
                noteForMaster={noteForMaster}
                interactive={false}
              />
            ) : (
              <div className="rounded-xl bg-background/20 p-3">
                <AsyncContentProgress loading label="Carregando oportunidade...">
                  {null}
                </AsyncContentProgress>
              </div>
            )
          ) : null}
        </div>

        {(() => {
          const authorMeta = (
            <AnalysisCardAuthorMeta
              name={displayAuthorName}
              avatarUrl={analysis.authorAvatarUrl}
              dateLabel={analysis.createdAt ? formatUserDateTime(analysis.createdAt) : null}
            />
          )

          if (!communityEnabled) {
            return <div className="mt-3 flex justify-end">{authorMeta}</div>
          }

          if (analysis.linkedOpportunityId !== null) {
            const oid = analysis.linkedOpportunityId
            const symbol = linkedOpportunity?.symbol ?? String(oid)

            return (
              <div className="mt-3">
                <OpportunityCommunityEntry
                  opportunityId={oid}
                  symbol={symbol}
                  name={linkedOpportunity?.name ?? null}
                  logoUrl={linkedOpportunity?.logo_url ?? null}
                  enabled={communityEnabled}
                  summary={opportunityCommunitySummary ?? null}
                  overlayContent={(
                    <AnalysisLinkedOpportunityHistory
                      opportunityId={analysis.linkedOpportunityId}
                      linkedOpportunity={linkedOpportunity}
                      thermoAllowed={thermoAllowed}
                      noteForMaster={noteForMaster}
                    />
                  )}
                  renderFooter={(summaryRow) => (
                    <div className="mt-2 flex w-full items-start justify-between gap-2">
                      <div className="min-w-0 flex flex-1 -mt-2">{summaryRow}</div>
                      <div className="flex flex-none items-start justify-end">
                        {authorMeta}
                      </div>
                    </div>
                  )}
                />
              </div>
            )
          }

          return (
            <AnalysisCommunityEntry
              analysisId={analysis.id}
              threadId={timelineRootId ?? analysis.id}
              title={analysis.title}
              contentHtml={analysis.contentHtml}
              attachments={analysis.attachments}
              enabled={communityEnabled}
              summary={analysisCommunitySummary}
              focusMessageId={focusMessageId}
              footerRight={authorMeta}
              overlayContent={
                showTimelineOverlay
                  ? (
                      <AnalysisTimelineHistory
                        timelineRootId={timelineRootId ?? analysis.id}
                      />
                    )
                  : undefined
              }
            />
          )
        })()}

        {canManagePosts && (
          <NewAnalysisModal
            open={editOpen}
            onOpenChange={setEditOpen}
            mode="edit"
            analysisId={analysis.id}
            initialTitle={analysis.title}
            initialHtml={analysis.contentHtml}
            initialAttachments={analysis.attachments}
            initialLinkedOpportunityId={analysis.linkedOpportunityId}
            initialLinkedTimelineRootId={analysis.linkedTimelineRootId}
            initialCourtesyEnabled={analysis.courtesyEnabled}
            onSubmit={handleEdit}
          />
        )}
        <Modal
          open={deleteOpen}
          onOpenChange={(v) => {
            if (!v && !deleting) setDeleteOpen(false)
          }}
          dismissible={!deleting}
          className="max-w-md"
        >
          <div className="space-y-4">
            <div className="text-lg font-semibold">Excluir análise</div>
            <p className="text-sm text-foreground/80">
              Tem certeza que deseja excluir esta análise?
            </p>
            <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-foreground/80">
              Essa exclusão não poderá ser desfeita.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleting}>
                {deleting ? 'Excluindo...' : 'Excluir'}
              </Button>
            </div>
          </div>
        </Modal>
      </CardContent>
    </Card>
  )
}
